import React, { useEffect, useState } from 'react';
import * as api from '../services/api';
import { Receipt, RESULT_LABEL, PROVENANCE, formatValue, receiptText, shortHash, when } from '../utils/evidenceReceipt';

/**
 * PR-1033: the evidence receipt for one check, opened from a check row.
 *
 * The drawer shows the tenant's own data unredacted (the audit view already does).
 * Anything that leaves the dashboard (copy, print, export) is redacted by default:
 * hostnames, account names, SIDs, IP and MAC addresses become stable placeholders.
 * User-facing wording follows decision 73: "Required", never framework names.
 */

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
}

const EvidenceReceipt: React.FC<{
  executionId: string;
  checkId: string;
  checkName: string;
  onClose: () => void;
}> = ({ executionId, checkId, checkName, onClose }) => {
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redactShared, setRedactShared] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setReceipt(null);
    setError(null);
    api.getCheckEvidence(executionId, checkId, false)
      .then(r => { if (!cancelled) setReceipt(r as Receipt); })
      .catch(() => { if (!cancelled) setError('No receipt is available for this check.'); });
    return () => { cancelled = true; };
  }, [executionId, checkId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const shared = async (): Promise<Receipt> =>
    (redactShared ? await api.getCheckEvidence(executionId, checkId, true) : receipt) as Receipt;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(receiptText(await shared()));
      setNotice(redactShared ? 'Copied (redacted)' : 'Copied (not redacted)');
    } catch {
      setNotice('Copy failed');
    }
  };

  const exportJson = async () => {
    try {
      const r = await shared();
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${r.check.id.replace(/[^a-z0-9-]+/gi, '_')}-${r.executionId.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setNotice(redactShared ? 'Exported (redacted)' : 'Exported (not redacted)');
    } catch {
      setNotice('Export failed');
    }
  };

  const print = async () => {
    try {
      const r = await shared();
      const w = window.open('', '_blank', 'width=820,height=900');
      if (!w) { setNotice('Allow pop-ups to print'); return; }
      w.document.write(`<!doctype html><html><head><title>Evidence receipt ${escapeHtml(r.check.id)}</title>
<style>body{font:12px/1.5 ui-monospace,Consolas,monospace;margin:32px;color:#111}pre{white-space:pre-wrap}</style></head>
<body><pre>${escapeHtml(receiptText(r))}</pre></body></html>`);
      w.document.close();
      w.focus();
      w.print();
    } catch {
      setNotice('Print failed');
    }
  };

  const c = receipt?.check;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={`Evidence receipt: ${checkName}`}>
      <button className="absolute inset-0 bg-black/50" aria-label="Close receipt" onClick={onClose} />
      <div className="relative w-full max-w-xl h-full overflow-y-auto bg-anchor-card border-l border-white/[0.08] p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Evidence receipt</div>
            <h3 className="text-base font-medium text-white break-words">{checkName}</h3>
            <div className="text-[11px] font-mono text-white/35 break-all">{checkId}</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/70 text-sm px-2" aria-label="Close">x</button>
        </div>

        {error && <p className="text-sm text-white/50">{error}</p>}
        {!receipt && !error && <p className="text-sm text-white/30">Loading receipt...</p>}

        {receipt && c && receipt.evidence === 'legacy' && (
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 text-xs text-white/55">
            No receipt: this audit predates evidence recording (agent earlier than 3.8). The result below is the module's own verdict.
          </div>
        )}

        {receipt && c && (
          <>
            <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-xs">
              <dt className="text-white/35">Result</dt>
              <dd className={`font-medium ${RESULT_LABEL[c.status].text}`}>{RESULT_LABEL[c.status].label}</dd>

              <dt className="text-white/35">Required</dt>
              <dd className="text-white/70 break-words">{formatValue(c.required, null)}</dd>

              <dt className="text-white/35">Observed</dt>
              <dd className="text-white/80">
                <pre className="whitespace-pre-wrap break-words font-mono text-[11px]">{formatValue(c.observed, c.provenance)}</pre>
                {c.observedTruncated && c.observedCount !== null && (
                  <div className="text-[10px] text-white/35 mt-0.5">First 50 of {c.observedCount} shown</div>
                )}
              </dd>

              <dt className="text-white/35">Provenance</dt>
              <dd>
                {c.provenance ? (
                  <>
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${PROVENANCE[c.provenance].cls}`}>{PROVENANCE[c.provenance].label}</span>
                    <span className="ml-2 text-white/45">{PROVENANCE[c.provenance].meaning}</span>
                    {c.basis && <div className="text-white/60 mt-1 break-words">Basis: {c.basis}</div>}
                  </>
                ) : (
                  <span className="text-white/40">Not recorded for this check</span>
                )}
              </dd>

              <dt className="text-white/35">Collected</dt>
              <dd className="text-white/70 break-all font-mono text-[11px]">
                {c.source ? <><span className="text-white/45">{c.source.kind}</span> {c.source.target}</> : <span className="font-sans text-white/40">Not recorded</span>}
              </dd>

              <dt className="text-white/35">Recorded</dt>
              <dd className="text-white/60">{when(receipt.audit.completedAt)}</dd>

              <dt className="text-white/35">Agent</dt>
              <dd className="text-white/60">{receipt.audit.agentVersion ? `version ${receipt.audit.agentVersion}` : 'version not recorded'}</dd>

              <dt className="text-white/35">Server</dt>
              <dd className="text-white/60">{receipt.crossCheck.note}</dd>
            </dl>

            {receipt.facts.length > 0 && (
              <details className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
                <summary className="text-[10px] font-medium text-white/40 uppercase tracking-wider cursor-pointer">
                  Audit context: {receipt.facts.length} pre-flight facts
                </summary>
                <table className="w-full mt-2 text-[11px]">
                  <tbody>
                    {receipt.facts.map(f => (
                      <tr key={f.key} className="align-top border-t border-white/[0.04]">
                        <td className="py-1 pr-2 font-mono text-white/55">{f.key}</td>
                        <td className="py-1 pr-2 text-white/75 break-words">
                          {f.status === 'collected' ? formatValue(f.value, 'observed') : <span className="text-white/40">{f.status}{f.failureReason ? `: ${f.failureReason}` : ''}</span>}
                        </td>
                        <td className="py-1 text-white/35 break-all">{[f.method, f.target].filter(Boolean).join(' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            <div className="text-[11px] text-white/40 break-all">
              Receipt hash <span className="font-mono text-white/60">sha256 {shortHash(receipt.receiptHash)}</span>
            </div>

            <div className="flex items-center flex-wrap gap-2 pt-2 border-t border-white/[0.06]">
              <label className="flex items-center gap-1.5 text-[11px] text-white/55 mr-auto">
                <input type="checkbox" checked={redactShared} onChange={e => setRedactShared(e.target.checked)} />
                Redact identifiers when sharing
              </label>
              <button onClick={copy} className="text-xs px-2.5 py-1 rounded border border-white/[0.1] text-white/70 hover:bg-white/[0.05]">Copy</button>
              <button onClick={print} className="text-xs px-2.5 py-1 rounded border border-white/[0.1] text-white/70 hover:bg-white/[0.05]">Print</button>
              <button onClick={exportJson} className="text-xs px-2.5 py-1 rounded border border-white/[0.1] text-white/70 hover:bg-white/[0.05]">Export JSON</button>
            </div>
            {notice && <div className="text-[11px] text-white/45" role="status">{notice}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default EvidenceReceipt;
